import React, { useEffect, useMemo, useState } from "react";
import { X, ExternalLink, ArrowUp, ArrowDown, Minus, Sparkles } from "lucide-react";
import api from "@/lib/api";

const ENGINES = [
  { key: "openai", label: "ChatGPT", bg: "#E8F4F0", fg: "#10A37F", letter: "G" },
  { key: "anthropic", label: "Claude", bg: "#FBEBE2", fg: "#D97757", letter: "C" },
  { key: "gemini", label: "Gemini", bg: "#E8EFFB", fg: "#4285F4", letter: "g" },
  { key: "perplexity", label: "Perplexity", bg: "#E5F0F2", fg: "#20808D", letter: "p" },
  { key: "google_aio", label: "Google AIO", bg: "#EFEAFA", fg: "#7B5BD9", letter: "A" },
];

function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Highlight brand + competitor mentions inside a plain-text response. */
function HighlightedText({ text, brand, competitors }) {
  const html = useMemo(() => {
    if (!text) return null;
    const names = [];
    if (brand) names.push({ name: brand, klass: "mention you" });
    (competitors || []).forEach((c) => {
      if (c) names.push({ name: c, klass: "mention" });
    });
    if (!names.length) return [text];
    // Build a combined regex with alternation, longest names first to avoid overlap.
    names.sort((a, b) => b.name.length - a.name.length);
    const pattern = new RegExp(`\\b(${names.map((n) => escapeRegex(n.name)).join("|")})\\b`, "gi");
    const out = [];
    let lastIdx = 0;
    let m;
    while ((m = pattern.exec(text)) !== null) {
      if (m.index > lastIdx) out.push(text.slice(lastIdx, m.index));
      const matched = m[0];
      const nameDef = names.find((n) => n.name.toLowerCase() === matched.toLowerCase());
      out.push(
        <span key={out.length} className={nameDef ? nameDef.klass : "mention"}>
          {matched}
        </span>
      );
      lastIdx = m.index + matched.length;
    }
    if (lastIdx < text.length) out.push(text.slice(lastIdx));
    return out;
  }, [text, brand, competitors]);

  if (!html) return null;
  return (
    <div
      style={{
        fontSize: 14.5,
        lineHeight: 1.65,
        color: "var(--aw-ink-2)",
        whiteSpace: "pre-wrap",
      }}
    >
      {html}
    </div>
  );
}

function Sparkline({ data, height = 40 }) {
  if (!data || data.length === 0) {
    return (
      <div
        style={{
          height,
          fontSize: 11,
          color: "var(--aw-muted)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        No history yet.
      </div>
    );
  }
  const width = 280;
  const stepX = data.length > 1 ? width / (data.length - 1) : 0;
  // Use position when present, else binary mentioned 0/1. Lower position is better.
  const positions = data
    .map((d) => (d.position ? d.position : null))
    .filter((p) => p !== null);
  const maxPos = Math.max(5, ...positions, 1);
  const points = data.map((d, i) => {
    const x = i * stepX;
    let y;
    if (d.position) {
      // Map position to y: position 1 → near top, max → near bottom
      y = ((d.position - 1) / Math.max(1, maxPos - 1)) * (height - 8) + 4;
    } else if (d.mentioned) {
      y = (height - 8) * 0.3 + 4;
    } else {
      y = height - 4;
    }
    return { x, y, mentioned: d.mentioned, position: d.position };
  });
  const polyline = points.map((p) => `${p.x},${p.y}`).join(" ");
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <polyline
        points={polyline}
        fill="none"
        stroke="var(--aw-accent)"
        strokeWidth={1.5}
        strokeLinejoin="round"
      />
      {points.map((p, i) => (
        <circle
          key={i}
          cx={p.x}
          cy={p.y}
          r={p.mentioned ? 2.5 : 1.5}
          fill={p.mentioned ? "var(--aw-accent)" : "var(--aw-muted-2)"}
        />
      ))}
    </svg>
  );
}

function DiffItem({ diff }) {
  const arrow =
    diff.direction === "up" ? (
      <ArrowUp size={12} style={{ color: "var(--aw-accent-ink)" }} />
    ) : diff.direction === "down" ? (
      <ArrowDown size={12} style={{ color: "#C84A2F" }} />
    ) : (
      <Minus size={12} style={{ color: "var(--aw-muted)" }} />
    );
  const tone =
    diff.direction === "up"
      ? "var(--aw-accent-ink)"
      : diff.direction === "down"
      ? "#C84A2F"
      : "var(--aw-muted)";
  return (
    <li className="flex items-start gap-2 text-sm py-1.5">
      <span style={{ marginTop: 4 }}>{arrow}</span>
      <span style={{ color: tone, fontWeight: 500 }}>{diff.text}</span>
    </li>
  );
}

export default function PromptDetailDrawer({ open, promptId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeEngine, setActiveEngine] = useState("openai");

  useEffect(() => {
    if (!open || !promptId) {
      setData(null);
      return;
    }
    setLoading(true);
    api
      .get(`/prompts/${promptId}/detail`)
      .then((r) => {
        setData(r.data);
        if (r.data?.engines?.length) {
          setActiveEngine(r.data.engines[0].engine);
        }
      })
      .finally(() => setLoading(false));
  }, [open, promptId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const engineData =
    data?.engines?.find((e) => e.engine === activeEngine) || data?.engines?.[0];
  const meta = ENGINES.find((e) => e.key === activeEngine) || ENGINES[0];

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        data-testid="prompt-drawer-backdrop"
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(20, 18, 12, 0.32)",
          backdropFilter: "blur(2px)",
          zIndex: 60,
          animation: "fadeIn 0.18s ease",
        }}
      />
      {/* Drawer */}
      <aside
        data-testid="prompt-drawer"
        role="dialog"
        aria-label="Prompt detail"
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          height: "100vh",
          width: "min(720px, 100vw)",
          background: "var(--aw-bg)",
          borderLeft: "1px solid var(--aw-border)",
          zIndex: 70,
          boxShadow: "-24px 0 64px -20px rgba(20, 18, 12, 0.25)",
          display: "flex",
          flexDirection: "column",
          animation: "slideInRight 0.24s ease",
        }}
      >
        {/* Header */}
        <header
          style={{
            padding: "18px 24px",
            borderBottom: "1px solid var(--aw-border)",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
            background: "var(--aw-surface)",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="eyebrow">Prompt</div>
            {data?.prompt ? (
              <h2
                className="mt-1"
                style={{
                  fontFamily: "var(--font-display)",
                  fontStyle: "italic",
                  fontSize: 22,
                  lineHeight: 1.25,
                  color: "var(--aw-ink)",
                  margin: 0,
                }}
              >
                "{data.prompt.text}"
              </h2>
            ) : (
              <div className="mt-1 text-sm" style={{ color: "var(--aw-muted)" }}>
                Loading…
              </div>
            )}
            {data?.is_seed && (
              <div
                className="mt-2 inline-flex items-center gap-1.5 text-xs"
                style={{ color: "var(--aw-accent-ink)" }}
                data-testid="prompt-drawer-seed-banner"
              >
                <Sparkles size={12} /> Showing demo data — run this prompt to see live results.
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            data-testid="prompt-drawer-close"
            aria-label="Close"
            style={{
              width: 36,
              height: 36,
              borderRadius: 999,
              border: "1px solid var(--aw-border)",
              background: "white",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--aw-muted)",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <X size={16} />
          </button>
        </header>

        {/* Engine tabs */}
        {data?.engines && (
          <div
            data-testid="prompt-drawer-engine-tabs"
            style={{
              padding: "0 24px",
              borderBottom: "1px solid var(--aw-border)",
              background: "var(--aw-surface)",
              display: "flex",
              gap: 4,
              overflowX: "auto",
            }}
          >
            {ENGINES.filter((e) => data.engines.some((d) => d.engine === e.key)).map((e) => {
              const isActive = activeEngine === e.key;
              const eng = data.engines.find((d) => d.engine === e.key);
              const mentioned = eng?.latest?.mentioned_brand;
              return (
                <button
                  key={e.key}
                  onClick={() => setActiveEngine(e.key)}
                  data-testid={`prompt-drawer-tab-${e.key}`}
                  style={{
                    padding: "12px 14px",
                    border: 0,
                    background: "transparent",
                    color: isActive ? "var(--aw-ink)" : "var(--aw-muted)",
                    fontWeight: isActive ? 500 : 400,
                    fontSize: 13,
                    cursor: "pointer",
                    borderBottom: `2px solid ${
                      isActive ? "var(--aw-ink)" : "transparent"
                    }`,
                    marginBottom: -1,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    whiteSpace: "nowrap",
                  }}
                >
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 4,
                      background: e.bg,
                      color: e.fg,
                      fontFamily: "var(--font-mono)",
                      fontSize: 10,
                      fontWeight: 600,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {e.letter}
                  </span>
                  {e.label}
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 999,
                      background: mentioned
                        ? "var(--aw-accent)"
                        : "var(--aw-muted-2)",
                    }}
                  />
                </button>
              );
            })}
          </div>
        )}

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
          {loading ? (
            <div className="text-sm" style={{ color: "var(--aw-muted)" }}>
              Loading detail…
            </div>
          ) : !engineData ? (
            <div className="text-sm" style={{ color: "var(--aw-muted)" }}>
              No data for this engine.
            </div>
          ) : (
            <>
              {/* Top row: status + sparkline */}
              <div
                className="grid gap-4 mb-5"
                style={{ gridTemplateColumns: "1.2fr 1fr" }}
                data-testid="prompt-drawer-summary"
              >
                <div className="card-aw">
                  <div className="eyebrow">Latest result</div>
                  <div className="mt-2 flex items-baseline gap-3 flex-wrap">
                    <span
                      style={{
                        fontFamily: "var(--font-display)",
                        fontSize: 30,
                        lineHeight: 1,
                      }}
                    >
                      {engineData.latest?.mentioned_brand ? (
                        <>
                          #{engineData.latest.mention_position || "?"}{" "}
                          <span style={{ fontSize: 14, color: "var(--aw-muted)" }}>
                            mentioned
                          </span>
                        </>
                      ) : (
                        <span style={{ color: "var(--aw-muted)" }}>Not mentioned</span>
                      )}
                    </span>
                  </div>
                  <div
                    className="mt-2 mono text-xs"
                    style={{ color: "var(--aw-muted)", letterSpacing: "0.04em" }}
                  >
                    {engineData.latest?.created_at
                      ? new Date(engineData.latest.created_at).toLocaleString()
                      : "Just now"}
                    {engineData.latest?.sentiment && (
                      <>
                        {" · "}
                        <span
                          style={{
                            color:
                              engineData.latest.sentiment === "positive"
                                ? "var(--aw-accent-ink)"
                                : engineData.latest.sentiment === "negative"
                                ? "#C84A2F"
                                : "var(--aw-muted)",
                          }}
                        >
                          {engineData.latest.sentiment}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div className="card-aw">
                  <div className="eyebrow">28-day position</div>
                  <div className="mt-2">
                    <Sparkline data={engineData.sparkline || []} />
                  </div>
                  <div
                    className="mt-1 mono text-xs"
                    style={{ color: "var(--aw-muted)", letterSpacing: "0.04em" }}
                  >
                    {engineData.sparkline?.filter((s) => s.mentioned).length || 0} mentions / {engineData.sparkline?.length || 0} runs
                  </div>
                </div>
              </div>

              {/* What changed */}
              {engineData.diff?.length > 0 && (
                <div className="card-aw mb-5" data-testid="prompt-drawer-diff">
                  <div className="eyebrow mb-2">What changed</div>
                  <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                    {engineData.diff.map((d, i) => (
                      <DiffItem key={i} diff={d} />
                    ))}
                  </ul>
                </div>
              )}

              {/* AI answer with highlights */}
              <div className="card-aw card-aw-lg mb-5" data-testid="prompt-drawer-answer">
                <div className="flex items-center gap-2 mb-3">
                  <span
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 999,
                      background: meta.bg,
                      color: meta.fg,
                      fontFamily: "var(--font-mono)",
                      fontSize: 11,
                      fontWeight: 700,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {meta.letter}
                  </span>
                  <div
                    className="mono"
                    style={{
                      fontSize: 11,
                      color: "var(--aw-muted)",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    {meta.label} · raw answer
                  </div>
                </div>
                <HighlightedText
                  text={engineData.latest?.response_text || ""}
                  brand={data?.brand}
                  competitors={data?.competitors}
                />
              </div>

              {/* Citations */}
              {engineData.latest?.citations?.length > 0 && (
                <div className="card-aw mb-5" data-testid="prompt-drawer-citations">
                  <div className="eyebrow mb-3">
                    Citations · {engineData.latest.citations.length}
                  </div>
                  <ul
                    style={{
                      listStyle: "none",
                      padding: 0,
                      margin: 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                    }}
                  >
                    {engineData.latest.citations.slice(0, 20).map((url, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <ExternalLink size={11} style={{ color: "var(--aw-muted-2)", flexShrink: 0 }} />
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            color: "var(--aw-ink-2)",
                            fontFamily: "var(--font-mono)",
                            fontSize: 12.5,
                            wordBreak: "break-all",
                          }}
                        >
                          {url}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Sentiment reason */}
              {engineData.latest?.sentiment_reason && (
                <div
                  className="card-aw"
                  style={{ background: "var(--aw-surface-2)" }}
                  data-testid="prompt-drawer-sentiment-reason"
                >
                  <div className="eyebrow mb-1">Why this sentiment</div>
                  <p className="text-sm" style={{ color: "var(--aw-ink-2)", margin: 0 }}>
                    {engineData.latest.sentiment_reason}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </aside>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
}
